<template>
<div v-if="dataUser" class="profession-form">

  <template v-if="showUnavailabilityDatesProviderId && !isManager">
    <input-text v-model="internalDataUser.unavailability_dates_provider_id"
                :validationRules="matriculeRequiredValidationRules"
                :validationTranslation="$t('models.user.matricule')"
                fieldName="unavailability_dates_provider_id"
                fieldId="unavailability_dates_provider_id"
                fieldType="text"
                :label="providerIdLabel"
                :required="matriculeRequired"
                :disabled="false"
                @input="onChange('unavailability_dates_provider_id', $event)" />
  </template>


  <template v-if="!isManager">
    <resource-type-field v-model="internalDataUser.resource_type"
                         :disabled="cannotEditService"
                         :field-name-addition="fieldNameAddition"
                         @input="onChange('resource_type', $event)" />
  </template>

  <template v-if="!isManager">
    <occupations-field :available-occupations="occupationsOptions"
                       :is-manager="isManager"
                       :user="internalDataUser"
                       :field-name-addition="fieldNameAddition"
                       @change="onChange('occupations', $event)" />
  </template>

  <services-with-hubs-filter :available-hubs="hubsOptions"
                             :availableServices="servicesOptions"
                             :disabledConditions="cannotEditService"
                             :dataUser="dataUser"
                             :isManager="isManager"
                             :isInternal="isInternal"
                             :small="small"
                             :collapseHubs="collapseHubs"
                             :field-name-addition="fieldNameAddition"
                             @change="onChange($event.field, $event.value)" />

  <div v-if="isRecruiter && cannotEditService" role="alert" class="alert alert-warning">
    {{ $t('EditUser.editServiceInfo') }}
  </div>

  <template v-if="isManager">
    <input-text v-model="internalDataUser.function"
                :fieldName="functionName"
                fieldType="text"
                :label="functionLabel"
                :required="false"
                :disabled="false"
                @input="onChange('function', $event)" />
  </template>
</div>
</template>

<script>
import { mapGetters } from "vuex";
import { roles } from '@/settings/roles'
import { checkRoleAdmin } from '@/helpers/roles'
import ResourceTypeField from '@/components/users/form/ResourceTypeField'
import OccupationsField from '@/components/users/form/OccupationsField'
import ServicesWithHubsFilter from '@/components/users/form/ServicesWithHubsFilter'
import InputText from '@/components/users/form/InputText'
import { unavailabilityDatesProviderIdLabel } from '@/helpers/entities'

export default {
  name: "ProfessionForm",
  props: {
    dataUser: Object,
    currentUser: Object,
    isSingleEntity: Boolean,
    isManager: Boolean,
    corporationHasProvider: Boolean, default: false,
    fieldNameAddition: { type: [String, Number], default: null },
    collapseHubs: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    availableServices: { type: Array, default: () => [] },
    availableOccupations: { type: Array, default: () => [] },
    availableHubs: { type: Array, default: () => [] }
  },
  inject: ['$validator'],
  components: {
    ResourceTypeField,
    OccupationsField,
    ServicesWithHubsFilter,
    InputText
  },
  data() {
    return {
      internalDataUser: Object.assign({}, this.dataUser)
    }
  },
  computed: {
    ...mapGetters({
      serverErrors: 'users/errors',
      hasErrors: 'users/hasErrors',
    }),
    isInternal(){
      return this.internalDataUser.resource_type == 'intern'
    },
    cannotEditService() {
      return (this.isRecruiter && this.$route.name == 'EditUser') ||
        (this.isAdmin && !this.adminManagesAllUserServices());
    },
    servicesOptions() {
      if(this.cannotEditService && this.dataUser.services && this.dataUser.services.length > 0) {
        return this.dataUser.services
      }

      return this.availableServices;
    },
    hubsOptions() {
      if(this.cannotEditService && this.dataUser.entity && this.dataUser.entity.hubs) {
        return this.dataUser.entity.hubs
      }

      return this.availableHubs;
    },
    occupationsOptions() {
      return this.availableOccupations;
    },
    randomID() {
      return Math.floor((Math.random() * 100) + 1);
    },
    functionName() {
      if(this.fieldNameAddition == null || this.fieldNameAddition == undefined) {
        return `${this.randomID}function`;
      }
      return `${this.fieldNameAddition}-function`
    },
    functionLabel() {
      return "Fonction"
    },
    isRecruiter() {
      return this.currentUser && this.currentUser.role == roles.recruiter;
    },
    isAdmin() {
      return this.currentUser && checkRoleAdmin(this.currentUser.role);
    },
    showUnavailabilityDatesProviderId() {
      return this.dataUser.entity &&
             !this.corporationHasProvider &&
             (this.dataUser.entity.unavailabilities_provider_scope === 'entity' ||
              this.dataUser.entity.unavailabilities_provider_scope === 'corporation');
    },
    providerIdLabel() {
      if (this.dataUser.entity) return unavailabilityDatesProviderIdLabel(this.dataUser.entity)
    },
    matriculeRequired() {
      return this.showUnavailabilityDatesProviderId &&
             this.dataUser.entity.mark_unavailability_dates_provider_required === true;
    },
    matriculeRequiredValidationRules() {
      return this.matriculeRequired ? 'required' : '';
    }
  },
  watch: {
    dataUser: {
      handler(){
        this.internalDataUser = Object.assign({}, this.dataUser)
        if (this.dataUser.resource_type == 'extern') {
          this.internalDataUser.affiliated_service = null
        }
      },
      deep: true
    },
    'dataUser.entity': function(newEntity, oldEntity) {
      if(newEntity && oldEntity && newEntity.id != oldEntity.id) {
        this.resetForm();
      }
    }
  },
  methods: {
    onChange(fieldName, value) {
      this.internalDataUser[fieldName] = value
      this.$emit('change', this.internalDataUser)
    },
    async resetForm() {
      if(this.currentUser && this.currentUser.id && this.dataUser.entity) {
        let entity_id = this.dataUser.entity.id
        await this.$store.dispatch('users/initManageableOptions', { entityId: entity_id })
      }
    },
    adminManagesAllUserServices() {
      const adminServiceIds = this.availableServices.map(service => service.id);
      return this.dataUser.services.filter(service => !adminServiceIds.includes(service.id)).length == 0;
    }
  },
  mounted() {
    this.resetForm()
  }
}
</script>

<style scoped>
  .resource-type label.label_internal {
    text-transform: none;
  }

  .accept-cgu a {
    text-decoration: underline;
  }
</style>



// WEBPACK FOOTER //
// src/components/candidate/ProfessionForm.vue